import 'client-only';

import CheckmarkIcon from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import ErrorIcon from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import { atom, useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { findCardByIdentifier } from '@/helpers/productSelectionAndCheckout/findCardByIdentifier';
import { isCardSelected as baseIsCardSelected } from '@/helpers/productSelectionAndCheckout/isCardSelected';
import {
  type Patient,
  type Card,
  type CardIdentifier,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { useProductSelectionAndCheckoutCommonData } from './useProductSelectionAndCheckoutCommonData';
import { usePurchaseContext } from './usePurchaseContext';
import { useRxFullSizeItemId } from './useRxFullSizeItemId';

const userSelectedItemIdsAtom = atom<undefined | number[]>(undefined);

type ItemIdUpdate = {
  ids: number | number[];
  mode: 'add' | 'remove';
  alternateIds?: undefined;
};

type ItemIdSwap = {
  ids: number | number[];
  mode: 'swap';
  alternateIds: number | number[];
};

export const useSelectedItemIds = () => {
  const { initialSelectedItemIds, cards } =
    useProductSelectionAndCheckoutCommonData();
  const { purchaseContext } = usePurchaseContext();
  const rXFullSizeItemId = useRxFullSizeItemId();

  const [userSelectedItemIds, setUserSelectedItemIds] = useAtom(
    userSelectedItemIdsAtom,
  );

  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  const flash = (message: string, state: 'success' | 'critical') => {
    const FlashIcon = state === 'critical' ? ErrorIcon : CheckmarkIcon;
    setFlashMessages([
      ...flashMessages,
      {
        icon: <FlashIcon />,
        iconSize: 'sm',
        title: message,
        state,
      },
    ]);
  };

  const selectedItemIds = useMemo(() => {
    if (purchaseContext === 'onetime' && rXFullSizeItemId) {
      return [rXFullSizeItemId];
    }

    return userSelectedItemIds ?? initialSelectedItemIds;
  }, [
    purchaseContext,
    rXFullSizeItemId,
    initialSelectedItemIds,
    userSelectedItemIds,
  ]);

  const isItemIdSelected = useCallback(
    (itemId: number) => selectedItemIds.includes(itemId),
    [selectedItemIds],
  );

  const isCardSelected = useCallback(
    (card: Card | CardIdentifier) => baseIsCardSelected(selectedItemIds, card),
    [selectedItemIds],
  );

  const selectedCards = useMemo(() => {
    return cards.filter(isCardSelected);
  }, [cards, isCardSelected]);

  const selectedRxCards = useMemo(() => {
    return cards.filter(isCardSelected).filter((card) => card.isRx);
  }, [cards, isCardSelected]);

  const findCard = useCallback(
    (cardIdentifier: CardIdentifier) =>
      findCardByIdentifier(cards, cardIdentifier),
    [cards],
  );

  const updateSelectedItemIds = useCallback(
    (...updates: (ItemIdUpdate | ItemIdSwap)[]) => {
      if (purchaseContext === 'onetime') {
        throw new Error('Cannot select items in onetime purchase context');
      }

      const newSelectedItemIds = new Set(selectedItemIds);

      const updateIds = (inputIds: number[], action: 'add' | 'delete') =>
        inputIds.forEach((itemId) => {
          newSelectedItemIds[action](itemId);
        });

      updates.forEach(({ ids, mode, alternateIds }) => {
        const inputIds = Array.isArray(ids) ? ids : [ids];

        if (mode === 'swap') {
          const inputAlternateIds = Array.isArray(alternateIds)
            ? alternateIds
            : [alternateIds];
          updateIds(inputIds, 'delete');
          updateIds(inputAlternateIds, 'add');
        } else if (mode === 'add') {
          updateIds(inputIds, 'add');
        } else {
          updateIds(inputIds, 'delete');
        }
      });

      setUserSelectedItemIds(Array.from(newSelectedItemIds.values()));
    },
    [purchaseContext, selectedItemIds, setUserSelectedItemIds],
  );

  const toggleCard = useCallback(
    ({ card, patient }: { card: Card; patient: Patient }) => {
      const updates: ItemIdUpdate[] = [];
      const cardSelected = isCardSelected(card);
      const mode = cardSelected ? 'remove' : 'add';
      if (card.fullSizeItemId) {
        updates.push({ ids: card.fullSizeItemId, mode });
      }
      if (card.fullSizeItemIds) {
        updates.push({ ids: card.fullSizeItemIds, mode });
      }
      updateSelectedItemIds(...updates);

      if (cardSelected) {
        flash('Product removed from box', 'success');

        trackEvent(AnalyticsEvent.PRODUCT_REMOVED, {
          offerId: patient?.introOffer?.id,
          productName: card.name,
        });
      } else {
        flash('Product added to box', 'success');

        trackEvent(AnalyticsEvent.PRODUCT_ADDED, {
          offerId: patient?.introOffer?.id,
          productName: card.name,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flashMessages, isCardSelected, setFlashMessages, updateSelectedItemIds],
  );

  const swapCard = useCallback(
    ({
      card,
      patient,
      alternateCard,
    }: {
      card: Card;
      patient: Patient;
      alternateCard: Card;
    }) => {
      const cardSelected = isCardSelected(card);
      const alternateCardSelected = isCardSelected(alternateCard);
      const noCardSelected = !cardSelected && !alternateCardSelected;
      const ids = card.fullSizeItemId
        ? card.fullSizeItemId
        : card.fullSizeItemIds;
      const alternateIds = alternateCard.fullSizeItemId
        ? alternateCard.fullSizeItemId
        : alternateCard.fullSizeItemIds;

      if (noCardSelected || !ids || !alternateIds) {
        return flash(
          `Cannot swap ${card.name} and ${alternateCard.name}`,
          'critical',
        );
      }

      if (cardSelected) {
        updateSelectedItemIds({ ids, mode: 'swap', alternateIds });
      } else {
        updateSelectedItemIds({
          ids: alternateIds,
          mode: 'swap',
          alternateIds: ids,
        });
      }

      flash('Product switched', 'success');

      trackEvent(AnalyticsEvent.PRODUCT_REMOVED, {
        offerId: patient?.introOffer?.id,
        productName: cardSelected ? card.name : alternateCard.name,
      });

      trackEvent(AnalyticsEvent.PRODUCT_ADDED, {
        offerId: patient?.introOffer?.id,
        productName: cardSelected ? alternateCard.name : card.name,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flashMessages, isCardSelected, setFlashMessages, updateSelectedItemIds],
  );

  const addCard = useCallback(
    ({
      additionalCard,
      patient,
    }: {
      additionalCard: Card;
      patient: Patient;
    }) => {
      const additionalCardIds = additionalCard.fullSizeItemId
        ? additionalCard.fullSizeItemId
        : additionalCard.fullSizeItemIds;

      if (!additionalCardIds) {
        return flash(`Cannot add ${additionalCard.name}`, 'critical');
      }

      updateSelectedItemIds({ ids: additionalCardIds, mode: 'add' });

      flash('Product added', 'success');

      trackEvent(AnalyticsEvent.PRODUCT_ADDED, {
        offerId: patient?.introOffer?.id,
        productName: additionalCard.name,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flashMessages, isCardSelected, setFlashMessages, updateSelectedItemIds],
  );

  return {
    selectedItemIds,
    isItemIdSelected,
    isCardSelected,
    findCard,
    selectedCards,
    selectedRxCards,
    updateSelectedItemIds,
    toggleCard,
    swapCard,
    addCard,
  } as const;
};
